import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ChevronUp, Plus, Receipt, Trash2 } from 'lucide-react'

import { AmountInput } from '@/components/AmountInput'
import { FieldLabel, FieldTooltip } from '@/components/FieldLabel'
import { Button } from '@/components/ui/button'
import { Card, CardAction, CardContent, CardHeader, CardIcon, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { MultiSelect } from '@/components/ui/multi-select'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { LayoutsApi } from '@/services/sites/layoutsApi'
import { TARGETING_TYPES } from '@/services/targeting/targeting'
import {
  emptyFeeDraft,
  FULL_FEE_TEAM_TYPES,
  newFeeKey,
  teamFeesToDraft,
  type AmountDrafts,
  type FeeDraft,
  type FeeTemplate,
  type FeeUnit,
  type LayoutFeeDraft,
  type TargetingFeeDraft,
  type TeamFeesDraft,
} from '@/services/users/teamFees'

/**
 * A fixed amount in the default currency, with the other currencies as
 * optional overrides. The API converts a missing currency from the default
 * one, so most fees only need the first field. Overrides that are set always
 * show, so nothing is hidden.
 */
function AmountFields({
  amounts,
  currencies,
  onChange,
}: {
  amounts: AmountDrafts
  currencies: string[]
  onChange: (amounts: AmountDrafts) => void
}) {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)
  const [primary, ...others] = currencies
  const hasOverrides = others.some((c) => (amounts[c] ?? '').trim() !== '')
  const shown = expanded || hasOverrides

  const amountInput = (currency: string) => (
    <AmountInput
      key={currency}
      className="w-36"
      value={amounts[currency] ?? ''}
      placeholder=""
      unit={currency}
      onChange={(v) => onChange({ ...amounts, [currency]: v })}
    />
  )

  if (!primary) return null
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        {amountInput(primary)}
        {others.length > 0 && !hasOverrides && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-muted-foreground"
            aria-expanded={shown}
            onClick={() => setExpanded(!shown)}
          >
            {shown ? <ChevronUp className="size-3.5" /> : <ChevronDown className="size-3.5" />}
            {shown ? t('teamFees.hideOtherCurrencies') : t('teamFees.otherCurrencies')}
          </Button>
        )}
        {others.length > 0 && (
          <FieldTooltip text={t('teamFees.conversionHelp', { currency: primary })} />
        )}
      </div>
      {shown && <div className="flex flex-wrap gap-2">{others.map(amountInput)}</div>}
    </div>
  )
}

/** A fee that is either a fixed amount per currency or a percentage. */
function FeeFields({
  fee,
  currencies,
  onChange,
}: {
  fee: FeeDraft
  currencies: string[]
  onChange: (fee: FeeDraft) => void
}) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-wrap items-start gap-2">
      <Tabs value={fee.unit} onValueChange={(v) => onChange({ ...fee, unit: v as FeeUnit })}>
        <TabsList aria-label={t('fees.feeType')}>
          <TabsTrigger value="MONETARY" title={t('teamFees.monetaryHelp')}>
            {t('fees.unitMonetary')}
          </TabsTrigger>
          <TabsTrigger value="PERCENTAGE" title={t('teamFees.percentageHelp')}>
            {t('fees.unitPercentage')}
          </TabsTrigger>
        </TabsList>
      </Tabs>
      {fee.unit === 'PERCENTAGE' ? (
        <AmountInput
          className="w-36"
          value={fee.percentage}
          placeholder=""
          max={100}
          unit="%"
          onChange={(v) => onChange({ ...fee, percentage: v })}
        />
      ) : (
        <AmountFields
          amounts={fee.amounts}
          currencies={currencies}
          onChange={(amounts) => onChange({ ...fee, amounts })}
        />
      )}
    </div>
  )
}

/** A list of fee rows, one line each: what the fee is for, its amount and a remove button. */
function FeeRows<T extends { key: number }>({
  label,
  help,
  hint,
  rows,
  onChange,
  newRow,
  addLabel,
  addDisabled,
  onAdd,
  renderRow,
}: {
  label: string
  help?: string
  hint?: string
  rows: T[]
  onChange: (rows: T[]) => void
  newRow: () => T
  addLabel?: string
  /** E.g. when every option already has a fee. */
  addDisabled?: boolean
  /** Called as a row is added, e.g. to start loading its options. */
  onAdd?: () => void
  renderRow: (row: T, update: (patch: Partial<T>) => void) => React.ReactNode
}) {
  const { t } = useTranslation()
  return (
    <FeeSection
      label={label}
      help={help}
      hint={hint}
      action={
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={addDisabled}
          onClick={() => {
            onAdd?.()
            onChange([...rows, newRow()])
          }}
        >
          <Plus className="size-3.5" />
          {addLabel ?? t('fees.addFee')}
        </Button>
      }
    >
      {rows.length === 0 ? (
        <p className="p-3 text-xs text-muted-foreground">{t('teamFees.none')}</p>
      ) : (
        <div className="flex flex-col divide-y divide-(--ad-border)">
          {rows.map((row) => (
            <div key={row.key} className="flex items-start gap-2 p-3">
              <div className="flex min-w-0 flex-1 flex-wrap items-start gap-2">
                {renderRow(row, (patch) =>
                  onChange(rows.map((r) => (r.key === row.key ? { ...r, ...patch } : r)))
                )}
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-8 shrink-0"
                aria-label={t('fees.removeFee')}
                title={t('fees.removeFee')}
                onClick={() => onChange(rows.filter((r) => r.key !== row.key))}
              >
                <Trash2 className="size-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </FeeSection>
  )
}

/** A titled, bordered group of fees inside the fees card. */
function FeeSection({
  label,
  help,
  hint,
  action,
  children,
}: {
  label: string
  help?: string
  /** Greyed out next to the title, e.g. the unit the fees are in. */
  hint?: string
  action?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <section className="overflow-hidden rounded-md border border-(--ad-border) bg-(--ad-surface)">
      <div className="flex min-h-12 items-center justify-between gap-2 border-b border-(--ad-border) px-3 py-2">
        <h3 className="flex items-center gap-1.5 text-sm font-medium">
          {label}
          {help && <FieldTooltip text={help} />}
          {hint && <span className="text-xs font-normal text-(--ad-text-muted)">{hint}</span>}
        </h3>
        {action}
      </div>
      {children}
    </section>
  )
}

interface TeamFeesFieldsProps {
  value: TeamFeesDraft
  onChange: (value: TeamFeesDraft) => void
  teamType: string
  /** The network's currencies, default first — a fixed fee is quoted in each. */
  currencies: string[]
}

/** The fee fields of a team or a fee template, without a card around them. */
export function TeamFeesFields({ value, onChange, teamType, currencies }: TeamFeesFieldsProps) {
  const { t } = useTranslation()
  const full = FULL_FEE_TEAM_TYPES.includes(teamType)
  const [layoutsWanted, setLayoutsWanted] = useState(false)

  const { data: layouts = [], isSuccess: layoutsLoaded } = useQuery({
    queryKey: ['layouts-all'],
    queryFn: () => LayoutsApi.getAll(),
    enabled: full && (layoutsWanted || value.layoutFees.length > 0),
  })

  const set = <K extends keyof TeamFeesDraft>(key: K, v: TeamFeesDraft[K]) =>
    onChange({ ...value, [key]: v })

  const targetingOptions = TARGETING_TYPES.map((def) => ({
    apiType: def.apiType,
    label: t(`targeting.types.${def.id}.plural`),
  }))
  const usedTargetingTypes = new Set(value.targetingFees.flatMap((row) => row.apiTypes))
  const usedLayoutIds = new Set(value.layoutFees.flatMap((row) => row.layouts.map((l) => l.id)))
  // Each type or layout has one fee, so a row only offers those no other row has.
  const targetingOptionsFor = (row: TargetingFeeDraft) =>
    targetingOptions
      .filter((o) => row.apiTypes.includes(o.apiType) || !usedTargetingTypes.has(o.apiType))
      .map((o) => ({ value: o.apiType, label: o.label }))
  const layoutOptionsFor = (row: LayoutFeeDraft) => {
    const own = new Set(row.layouts.map((l) => l.id))
    const loaded = new Set(layouts.map((l) => l.id))
    return [
      ...layouts.filter((l) => own.has(l.id) || !usedLayoutIds.has(l.id)),
      // Until the layouts load, or for a layout that is gone, the saved name labels the chip.
      ...row.layouts.filter((l) => !loaded.has(l.id)),
    ].map((l) => ({ value: l.id, label: l.name || l.id }))
  }

  return (
    <div className="@container flex flex-col gap-3">
      <FeeSection label={t('teamFees.baseFees')}>
        <div className="grid gap-4 p-3 sm:grid-cols-2">
          <div className="p-field">
            <FieldLabel tooltip={t('teamFees.techFeeHelp')}>{t('fees.techFee')}</FieldLabel>
            <FeeFields
              fee={value.techFee}
              currencies={currencies}
              onChange={(fee) => set('techFee', fee)}
            />
          </div>
          {full && (
            <div className="p-field">
              <FieldLabel tooltip={t('teamFees.creativeTagFeeHelp')} hint={t('fees.unitMonetary')}>
                {t('teamFees.creativeTagFee')}
              </FieldLabel>
              <AmountFields
                amounts={value.creativeTagFee}
                currencies={currencies}
                onChange={(amounts) => set('creativeTagFee', amounts)}
              />
            </div>
          )}
        </div>
      </FeeSection>

      {full && (
        <div className="grid items-start gap-3 @3xl:grid-cols-3">
          <FeeRows
            label={t('teamFees.supplementaryFees')}
            help={t('teamFees.supplementaryFeesHelp')}
            rows={value.supplementaryFees}
            onChange={(rows) => set('supplementaryFees', rows)}
            newRow={() => ({ ...emptyFeeDraft('PERCENTAGE'), key: newFeeKey(), name: '' })}
            renderRow={(row, update) => (
              <>
                <Input
                  className="w-full"
                  value={row.name}
                  placeholder={t('teamFees.feeNamePlaceholder')}
                  aria-label={t('fees.name')}
                  onChange={(e) => update({ name: e.target.value })}
                />
                <FeeFields fee={row} currencies={currencies} onChange={(fee) => update(fee)} />
              </>
            )}
          />

          <FeeRows
            label={t('teamFees.targetingFees')}
            help={t('teamFees.targetingFeesHelp')}
            rows={value.targetingFees}
            onChange={(rows) => set('targetingFees', rows)}
            newRow={() => ({ ...emptyFeeDraft('MONETARY'), key: newFeeKey(), apiTypes: [] })}
            addDisabled={targetingOptions.every((o) => usedTargetingTypes.has(o.apiType))}
            renderRow={(row, update) => (
              <>
                <MultiSelect
                  value={row.apiTypes}
                  onChange={(apiTypes) => update({ apiTypes })}
                  options={targetingOptionsFor(row)}
                  placeholder={t('teamFees.selectTargetingTypes')}
                />
                <FeeFields fee={row} currencies={currencies} onChange={(fee) => update(fee)} />
              </>
            )}
          />

          <FeeRows
            label={t('teamFees.creativeFormatFees')}
            help={t('teamFees.creativeFormatFeesHelp')}
            hint={t('fees.unitMonetary')}
            rows={value.layoutFees}
            onChange={(rows) => set('layoutFees', rows)}
            newRow={() => ({ key: newFeeKey(), layouts: [], amounts: {} })}
            addLabel={t('teamFees.addFormat')}
            addDisabled={layoutsLoaded && layouts.every((l) => usedLayoutIds.has(l.id))}
            onAdd={() => setLayoutsWanted(true)}
            renderRow={(row, update) => (
              <>
                <MultiSelect
                  value={row.layouts.map((l) => l.id)}
                  onChange={(ids) =>
                    update({
                      layouts: ids.map((id) => ({
                        id,
                        name:
                          layouts.find((l) => l.id === id)?.name ??
                          row.layouts.find((l) => l.id === id)?.name ??
                          '',
                      })),
                    })
                  }
                  options={layoutOptionsFor(row)}
                  placeholder={t('teamFees.selectLayouts')}
                />
                <AmountFields
                  amounts={row.amounts}
                  currencies={currencies}
                  onChange={(amounts) => update({ amounts })}
                />
              </>
            )}
          />
        </div>
      )}
    </div>
  )
}

interface TeamFeesCardProps extends TeamFeesFieldsProps {
  /** Network fee templates to start from; the picker only shows when there are some. */
  templates?: FeeTemplate[]
}

/** The default fees of a marketplace advertiser, creative agency or self-service team. */
export function TeamFeesCard({ templates = [], ...props }: TeamFeesCardProps) {
  const { t } = useTranslation()
  const [templateId, setTemplateId] = useState('')
  // Remount the fields on apply, so currency overrides open to match the new fees.
  const [applied, setApplied] = useState(0)

  return (
    <Card tone="money">
      <CardHeader>
        <CardTitle>
          <CardIcon icon={Receipt} />
          {t('teamFees.title')}
        </CardTitle>
        {templates.length > 0 && (
          <CardAction>
            <Select
              label={t('teamFees.applyTemplate')}
              value={templateId}
              onValueChange={(id) => {
                const template = templates.find((tpl) => tpl.id === id)
                if (!template) return
                setTemplateId(id)
                setApplied((n) => n + 1)
                props.onChange(teamFeesToDraft(template.fees))
              }}
            >
              <SelectTrigger size="sm" className="w-48">
                <SelectValue placeholder={t('teamFees.applyTemplate')}>
                  {templates.find((tpl) => tpl.id === templateId)?.name ?? null}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {templates.map((tpl) => (
                  <SelectItem key={tpl.id} value={tpl.id}>
                    {tpl.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardAction>
        )}
      </CardHeader>
      <CardContent>
        <TeamFeesFields key={applied} {...props} />
      </CardContent>
    </Card>
  )
}
